import { API_URL } from "./config";
import { getToken, subscribeToLogout } from "./auth";

/**
 * One decoded event from the ledger's push stream.
 *
 * The server sends a versioned envelope (`{v, seq, type, run_id, run_number, at,
 * data}`); `seq` is monotonic per replica and lets a client detect a gap, and
 * `runId` routes the invalidation to the affected run.
 */
export type LedgerEvent = {
  type: string;
  data: Record<string, unknown>;
  at: string;
  /** Per-replica sequence number; absent on the legacy poll transport. */
  seq?: number;
  runId?: string | null;
  runNumber?: number | null;
};

const MAX_DELAY_MS = 10_000;

/**
 * Every event name the server can send. Named SSE events are not delivered unless
 * a listener is registered, so this list is the client's half of the contract with
 * `qa_run_ledger.services.event_bus` (plus `resync` and the legacy `status_changed`).
 */
const NAMED_EVENTS = [
  "run_created",
  "run_status_changed",
  "status_changed",
  "result_recorded",
  "verdict_created",
  "verdict_reviewed",
  "verdict_updated",
  "verdict_pending",
  "coverage_updated",
  "reduction_complete",
  "scenario_changed",
  "finding_changed",
  "finding_graduated",
  "report_changed",
  "report_published",
  "notification_created",
  "notification_changed",
  "run_owners_changed",
  "resync",
] as const;

function eventsUrl(path: string): string | null {
  const token = getToken();
  const url = new URL(path, window.location.origin);
  if (API_URL.startsWith("http")) {
    const abs = new URL(path, API_URL.endsWith("/") ? API_URL : `${API_URL}/`);
    if (token) abs.searchParams.set("token", token);
    return abs.toString();
  }
  if (token) url.searchParams.set("token", token);
  return `${url.pathname}${url.search}`;
}

function decode(type: string, raw: string): LedgerEvent {
  let body: Record<string, unknown>;
  try {
    body = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return { type, data: { raw }, at: new Date().toISOString() };
  }
  const data =
    body.data && typeof body.data === "object" ? (body.data as Record<string, unknown>) : {};
  return {
    type: typeof body.type === "string" ? body.type : type,
    data,
    at: typeof body.at === "string" ? body.at : new Date().toISOString(),
    seq: typeof body.seq === "number" ? body.seq : undefined,
    runId: typeof body.run_id === "string" ? body.run_id : null,
    runNumber: typeof body.run_number === "number" ? body.run_number : null,
  };
}

/**
 * Connect to a ledger event stream, reconnecting with backoff.
 *
 * `EventSource` resends the last `id` it saw as `Last-Event-ID`, so the server can
 * replay the missed window or answer with a single `resync` when it cannot. A token
 * refresh reconnects with the new credential; a sign-out closes for good.
 */
export function connectLedgerEvents(
  path: string,
  onEvent: (event: LedgerEvent) => void,
): () => void {
  let closed = false;
  let source: EventSource | null = null;
  let delay = 500;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let tokenTimer: ReturnType<typeof setInterval> | null = null;
  let lastToken = getToken();

  const open = () => {
    if (closed) return;
    const url = eventsUrl(path);
    if (!url) {
      reconnectTimer = setTimeout(open, delay);
      delay = Math.min(delay * 2, MAX_DELAY_MS);
      return;
    }
    source = new EventSource(url);
    source.onopen = () => {
      delay = 500;
    };
    source.onerror = () => {
      source?.close();
      source = null;
      if (closed) return;
      reconnectTimer = setTimeout(open, delay);
      delay = Math.min(delay * 2, MAX_DELAY_MS);
    };
    for (const type of NAMED_EVENTS) {
      source.addEventListener(type, (ev: MessageEvent<string>) => {
        onEvent(decode(type, ev.data));
      });
    }
  };

  open();
  tokenTimer = setInterval(() => {
    const next = getToken();
    if (next !== lastToken) {
      lastToken = next;
      source?.close();
      source = null;
      delay = 500;
      open();
    }
  }, 15_000);

  const unsubLogout = subscribeToLogout(() => {
    closed = true;
    source?.close();
    source = null;
  });

  return () => {
    closed = true;
    unsubLogout();
    if (reconnectTimer) clearTimeout(reconnectTimer);
    if (tokenTimer) clearInterval(tokenTimer);
    source?.close();
    source = null;
  };
}
